import { ReactNode, useState } from 'react';

import { Autocomplete, AutocompleteChangeReason, AutocompleteRenderInputParams, SxProps, TextField, Theme, createFilterOptions } from '@mui/material';

// Minimal interface for items that can be selected
export interface SelectableItem {
  itemId: string;
  name: string;
}

type PeopleHookResult<T extends SelectableItem> = {
  items: T[] | undefined;
  isLoading: boolean;
  error: Error | undefined;
};

export interface PersonAutocompleteProps<T extends SelectableItem> {
  // Core props
  usePeopleHook: () => PeopleHookResult<T>;

  // Controlled/Uncontrolled
  value?: T | null;
  defaultValue?: T | null;
  onChange?: (event: React.SyntheticEvent, value: T | null, reason: AutocompleteChangeReason) => void;
  onInputEnter?: (value: T | null) => void;

  // Display
  label?: ReactNode;
  emptyLabel?: string;
  inviteNewPrompt?: string;
  placeholder?: string;
  size?: 'small' | 'medium';

  // Behavior
  filter?: (item: T) => boolean;
  disabled?: boolean;
  clearable?: boolean;
  clearOnBlur?: boolean;
  autoSelect?: boolean;
  autoHighlight?: boolean;
  onInviteNew?: (name: string) => void;
  onInputChange?: (value: string) => void;

  // Styling
  sx?: SxProps<Theme>;
  id?: string;

  // Advanced customization (optional)
  renderOption?: (props: React.HTMLAttributes<HTMLLIElement>, option: T) => ReactNode;
  renderInput?: (params: AutocompleteRenderInputParams) => ReactNode;
  slotProps?: object;
}

export default function PersonAutocomplete<T extends SelectableItem>(props: PersonAutocompleteProps<T>) {
  const {
    usePeopleHook,
    value,
    defaultValue,
    onChange,
    onInputEnter,
    label,
    emptyLabel,
    inviteNewPrompt = "Add new person",
    placeholder,
    size = 'medium',
    filter,
    disabled = false,
    clearable = true,
    clearOnBlur = true,
    autoSelect = true,
    autoHighlight = true,
    onInviteNew,
    onInputChange,
    sx,
    id,
    renderOption,
    renderInput,
    slotProps,
  } = props;

  const { items: people, isLoading, error } = usePeopleHook();
  const [inputValue, setInputValue] = useState('');

  // Apply filter if provided
  const baseOptions = people ? (filter ? people.filter(filter) : people) : [];

  const defaultFilter = createFilterOptions<T>();

  // Don't render until data is loaded
  if (isLoading || !people) {
    return null;
  }

  if (error) {
    console.error('Error loading people:', error);
    return null;
  }

  return (
    <Autocomplete
      id={id}
      size={size}
      options={baseOptions}
      filterOptions={(options, params) => {
        const filtered = defaultFilter(options, params);

        // If onInviteNew is provided and user has typed something, always add "Add new person" option at the bottom
        if (onInviteNew && params.inputValue.trim()) {
          filtered.push({
            itemId: `__add__${params.inputValue}`,
            name: `${inviteNewPrompt}`
          } as T);
        }

        return filtered;
      }}
      getOptionKey={(option) => option.itemId}
      value={value}
      defaultValue={defaultValue}
      inputValue={inputValue}
      onChange={(event, newValue, reason) => {
        // Check if this is the synthetic "Invite" option
        if (newValue && newValue.itemId.startsWith('__add__') && onInviteNew) {
          const nameToInvite = inputValue.trim();
          setInputValue(nameToInvite); // Set input to just the name
          onInviteNew(nameToInvite);
          return;
        }
        // Normal selection
        onChange?.(event, newValue, reason);
      }}
      onInputChange={(_event, newInputValue) => {
        setInputValue(newInputValue);
        onInputChange?.(newInputValue);
      }}
      clearOnBlur={clearOnBlur}
      disabled={disabled}
      disableClearable={!clearable}
      autoSelect={autoSelect}
      autoHighlight={autoHighlight}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => option.itemId === value?.itemId}
      onKeyDown={(event) => {
        // Check if Enter is pressed, dropdown is closed (no aria-expanded or false), and there's a value
        if (event.key === 'Enter' && onInputEnter && value) {
          const inputElement = event.currentTarget.querySelector('input');
          const isDropdownOpen = inputElement?.getAttribute('aria-expanded') === 'true';
          if (!isDropdownOpen) {
            event.preventDefault();
            onInputEnter(value);
          }
        }
      }}
      renderOption={
        renderOption ||
        ((props, option) => {
          const { key, ...otherProps } = props;
          return (
            <li key={key} {...otherProps}>
              {option.name}
            </li>
          );
        })
      }
      renderInput={
        renderInput ||
        ((params) => {
          const displayLabel = value || defaultValue || !emptyLabel ? label : emptyLabel;
          return <TextField {...params} variant="filled" label={displayLabel} placeholder={placeholder} />;
        })
      }
      sx={sx}
      slotProps={slotProps}
    />
  );
}
